import { Analysis, AnalysisItem } from '../types';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { expandAnalysis } from '../services/analysisService';
import { getSuggestedQuestions, clearSuggestedQuestions } from '../services/abTestService';
import { validateAndSendExpertStat, forceReloadLists } from '../services/expertValidationService';
import { calculateConfidence } from '../utils/confidenceCalculator';
import AIPromptModal from './AIPromptModal';
import ExpandAnalysisModal from './ExpandAnalysisModal';
import { LLMStatsDisplay } from './LLMStatsDisplay';
import { getAnalysisTaskIds } from '../services/resultStorageService';
import { AnalysisHeaderV3, type Expert, type LLMModel } from './AnalysisHeaderV3';
import { ProsConsV3 } from './ProsConsV3';
import { RecommendationV3 } from './RecommendationV3';
import { SuggestedQuestionsV3 } from './SuggestedQuestionsV3';
import { ImprovementSuggestionsV3 } from './ImprovementSuggestionsV3';
import { AnalysisRating } from './AnalysisRating';

interface AnalysisResultProps {
  analysis: Analysis;
  onItemMove: (itemId: string, newCategory: 'plus' | 'minus') => void;
  onRatingChange: (itemId: string, rating: number) => void;
  onItemDelete?: (itemId: string) => void;
  onAddItem?: (text: string, category: 'plus' | 'minus', rating: number) => void;
  onAnalysisUpdate?: (updatedAnalysis: Analysis) => void;
  isReadOnly?: boolean;
  onUseImprovedTemplate?: (template: string, expert?: string, person?: string, customExpert?: string, customPerson?: string, promptPackage?: string) => void;
  onRestartWithQuestion?: (question: string, expert?: string, person?: string, customExpert?: string, customPerson?: string) => void;
  currentTaskIds?: string[]; // taskId для статистики LLM
  theme?: 'dark' | 'light'; // Тема
  language?: 'ru' | 'en'; // Язык
  // Пропсы для шеринга
  onShare?: (analysis?: Analysis) => Promise<string | null>;
  onExportPDF?: (analysis?: Analysis) => Promise<void>;
  isSharing?: boolean;
  isExportingPDF?: boolean;
  shareUrl?: string | null;
  hasBeenShared?: boolean;
  shareError?: string | null;
  // Пропс для нового вопроса
  onNewQuestion?: () => void;
  // Пропс для shareId (для расшаренных страниц)
  shareId?: string;
  // Пропсы для переключения оригинального/изменённого анализа
  isOriginalModified?: boolean;
  showOriginal?: boolean;
  onToggleOriginal?: () => void;
  isTransitioning?: boolean;
  isLoadingOriginal?: boolean;
}

export default function AnalysisResult({ 
  analysis, 
  onItemMove, 
  onRatingChange, 
  onItemDelete, 
  onAddItem, 
  onAnalysisUpdate, 
  isReadOnly = false, 
  onUseImprovedTemplate, 
  onRestartWithQuestion, 
  currentTaskIds,
  theme = 'light',
  language = 'ru',
  // Пропсы для шеринга
  onShare,
  onExportPDF,
  isSharing = false,
  isExportingPDF = false,
  shareUrl,
  hasBeenShared = false,
  shareError,
  // Пропс для нового вопроса
  onNewQuestion,
  // Пропс для shareId
  shareId,
  // Пропсы для переключения оригинального/изменённого анализа
  isOriginalModified = false,
  showOriginal = false,
  onToggleOriginal,
  isTransitioning = false,
  isLoadingOriginal = false
}: AnalysisResultProps) {
  const { i18n } = useTranslation();

  const [originalAnalysis, setOriginalAnalysis] = useState<Analysis | null>(null);
  const [aiPromptModal, setAiPromptModal] = useState<{ isOpen: boolean; item: AnalysisItem | null }>({
    isOpen: false,
    item: null
  });
  const [expandModal, setExpandModal] = useState(false);
  const [isExpanding, setIsExpanding] = useState(false);
  const [suggestedQuestions, setSuggestedQuestions] = useState<any>(null);
  const [showSuggestedQuestions, setShowSuggestedQuestions] = useState(false);
  const [analysisTaskIds, setAnalysisTaskIds] = useState<string[]>([]);
  const [layoutMode, setLayoutMode] = useState<'single' | 'double'>('double');
  
  const plusItems = analysis.items.filter(item => item.category === 'plus');
  const minusItems = analysis.items.filter(item => item.category === 'minus');
  
  const totalPlusScore = plusItems.reduce((sum, item) => sum + item.rating, 0);
  const totalMinusScore = minusItems.reduce((sum, item) => sum + item.rating, 0);
  
  // Определяем результат для нового дизайна
  const getAnalysisResult = (): "YES" | "NO" | "MAYBE" => {
    if (totalPlusScore > totalMinusScore) return "YES";
    if (totalMinusScore > totalPlusScore) return "NO";
    return "MAYBE";
  };

  // Получаем исходный вердикт от ИИ (сохранен в объекте analysis)
  const getOriginalAnalysisResult = (): "YES" | "NO" | "MAYBE" => {
    // Используем originalDecision из объекта analysis, если он есть
    if (analysis.originalDecision) {
      return analysis.originalDecision;
    }
    
    // Fallback: если originalDecision нет, вычисляем на основе originalAnalysis
    if (originalAnalysis) {
      const originalPlusItems = originalAnalysis.items.filter(item => item.category === 'plus');
      const originalMinusItems = originalAnalysis.items.filter(item => item.category === 'minus');
      
      const originalTotalPlusScore = originalPlusItems.reduce((sum, item) => sum + item.rating, 0);
      const originalTotalMinusScore = originalMinusItems.reduce((sum, item) => sum + item.rating, 0);
      
      if (originalTotalPlusScore > originalTotalMinusScore) return "YES";
      if (originalTotalMinusScore > originalTotalPlusScore) return "NO";
      return "MAYBE";
    }
    
    // Если ничего нет, используем текущий результат (не должно происходить в нормальной работе)
    return getAnalysisResult();
  };

  // Создаем объект эксперта для нового дизайна
  const getExpert = (): Expert | undefined => {
    if (analysis.expert || analysis.person) {
      return {
        id: 'current-expert',
        name: analysis.expert || analysis.person || '',
        nameEn: analysis.expert || analysis.person || '',
        specialization: analysis.expert ? 'Эксперт' : 'Личность',
        specializationEn: analysis.expert ? 'Expert' : 'Personality',
        type: analysis.expert ? 'expert' : 'celebrity'
      };
    }
    return undefined;
  };

  // Создаем объект LLM модели (подпись "ИИ"/"AI" выводится через i18n в AnalysisHeaderV3 по id === 'ai')
  const getLLMModel = (): LLMModel | undefined => {
    if (!analysis.expert && !analysis.person) {
      return {
        id: 'ai',
        name: '',
        provider: 'AI',
        version: ''
      };
    }
    
    return {
      id: 'gpt-4',
      name: 'GPT',
      provider: 'OpenAI',
      version: '4.0'
    };
  };
  
  // Автоматическая прокрутка в начало страницы при монтировании компонента
  useEffect(() => {
    // Плавная прокрутка в начало страницы
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Сохраняем оригинальный анализ при первом рендере
  useEffect(() => {
    if (!originalAnalysis) {
      setOriginalAnalysis(JSON.parse(JSON.stringify(analysis)));
    }
  }, [analysis, originalAnalysis]);

  // Проверяем наличие предложенных вопросов для режима B
  useEffect(() => {
    const questions = getSuggestedQuestions();
    
    if (questions && !analysis.question) {
      // Показываем баннер если есть сохраненные вопросы и нет вопроса в анализе
      setSuggestedQuestions(questions);
      setShowSuggestedQuestions(true);
    }
  }, [analysis]);

  // Получаем taskId для статистики LLM
  useEffect(() => {
    const loadTaskIds = async () => {
      try {
        // Если переданы currentTaskIds (для текущего анализа), используем их
        if (currentTaskIds && currentTaskIds.length > 0) {
          setAnalysisTaskIds(currentTaskIds);
          return;
        }

        // Иначе пытаемся получить taskId из URL (для страницы результата)
        const path = window.location.pathname;
        const match = path.match(/\/result\/(.+)/);
        if (match) {
          const [, userIdAndNumber] = match;
          const analysisNumber = parseInt(userIdAndNumber.split('_').pop() || '0');
          if (analysisNumber > 0) {
            const taskIds = getAnalysisTaskIds(analysisNumber);
            setAnalysisTaskIds(taskIds);
          }
        }
      } catch (error) {
        console.error('Ошибка при получении taskId для статистики:', error);
      }
    };

    loadTaskIds();
  }, [analysis.external_id, currentTaskIds]);
  
 
  const handleExpandAnalysis = async (settings: {
    plusCount: number;
    minusCount: number;
  }) => {
    setIsExpanding(true);
    try {
      const response = await expandAnalysis({
        analysisId: analysis.external_id || analysis.id,
        plusCount: settings.plusCount,
        minusCount: settings.minusCount,
        currentAnalysis: analysis // передаем актуальные данные анализа
      });

      if (response.success && response.newItems.length > 0) {
        // Добавляем новые аргументы к существующим
        const updatedItems = [...analysis.items, ...response.newItems];
        
        // Обновляем анализ через callback (нужно будет добавить в props)
        if (onAnalysisUpdate) {
          onAnalysisUpdate({
            ...analysis,
            items: updatedItems
          });
        }
        
        setExpandModal(false);
        

      }
    } catch (error) {
      console.error('Error expanding analysis:', error);
      // Здесь можно добавить уведомление об ошибке
    } finally {
      setIsExpanding(false);
    }
  };

  // Обработчики для предложенных вопросов
  const handleSelectQuestion = async (question: string) => {
    if (onRestartWithQuestion && suggestedQuestions) {
      try {
        // 1. Сначала валидируем эксперта/личность и отправляем статистику
        await validateAndSendExpertStat(
          suggestedQuestions.expert,
          suggestedQuestions.person,
          suggestedQuestions.customExpert,
          suggestedQuestions.customPerson,
          i18n.language
        );
        
        // 2. Запускаем анализ с выбранным вопросом после валидации
        onRestartWithQuestion(
          question,
          suggestedQuestions.expert,
          suggestedQuestions.person,
          suggestedQuestions.customExpert,
          suggestedQuestions.customPerson
        );
        clearSuggestedQuestions();
        setShowSuggestedQuestions(false);
      } catch (error) {
        console.error('❌ AnalysisResult - Ошибка при валидации эксперта/личности при выборе вопроса:', error);
        // В случае ошибки валидации все равно запускаем анализ
        // (статистика уже отправлена в сервисе)
        
        onRestartWithQuestion(
          question,
          suggestedQuestions.expert,
          suggestedQuestions.person,
          suggestedQuestions.customExpert,
          suggestedQuestions.customPerson
        );
        clearSuggestedQuestions();
        setShowSuggestedQuestions(false);
      }
    }
  };






  const handleAddItem = (text: string, category: 'plus' | 'minus', rating: number) => {
    if (onAddItem) {
      onAddItem(text, category, rating);
    }
  };

  // Функции для нового дизайна
  const handleAddCustomArgument = (type: 'pro' | 'con', text: string, rating: number) => {
    const category = type === 'pro' ? 'plus' : 'minus';
    handleAddItem(text, category, rating);
  };

  const handleMoveArgument = (argumentId: string, fromType: 'pro' | 'con') => {
    const newCategory = fromType === 'pro' ? 'minus' : 'plus';
    onItemMove(argumentId, newCategory);
  };

  const handleUpdateArgumentRating = (argumentId: string, newRating: number, _type: 'pro' | 'con') => {
    onRatingChange(argumentId, newRating);
  };

  const handleOpenAIPrompt = (item: AnalysisItem) => {
    setAiPromptModal({ isOpen: true, item });
  };


  const handleCloseAIPrompt = () => {
    setAiPromptModal({ isOpen: false, item: null });
  };

  const handleUseImprovedTemplate = async () => {
    if (analysis.improvedRequest && onUseImprovedTemplate) {
      try {
        console.log('🔍 AnalysisResult - Обработка шаблона:', {
          expert: analysis.expert,
          person: analysis.person,
          promptPackage: analysis.promptPackage
        });
        
        // Загружаем актуальные списки экспертов и личностей для текущего языка
        const { experts, persons } = await forceReloadLists(i18n.language);
        
        console.log('🔍 AnalysisResult - Загружены списки:', {
          expertsCount: experts.length,
          personsCount: persons.length,
          expertValues: experts.map(e => e.value).slice(0, 5),
          expertLabels: experts.map(e => e.label).slice(0, 5)
        });
        
        let finalExpert: string | undefined = undefined;
        let finalPerson: string | undefined = undefined;
        let finalCustomExpert = '';
        let finalCustomPerson = '';
        
        // Используем promptPackage для определения типа выбора
        const promptPackageValue = analysis.promptPackage?.trim().toLowerCase();
        const isExpertPackage = promptPackageValue === 'expert';
        const isPersonPackage = promptPackageValue === 'person';
        
        console.log('🔍 AnalysisResult - promptPackage проверка:', {
          original: analysis.promptPackage,
          trimmed: promptPackageValue,
          isExpertPackage,
          isPersonPackage,
          expert: analysis.expert,
          person: analysis.person
        });
        
        // Обрабатываем expert только если promptPackage = 'expert'
        if (isExpertPackage && analysis.expert) {
          // Проверяем, является ли это экспертом из списка (по value или label)
          const matchedExpert = experts.find(e => 
            e.value === analysis.expert || 
            e.label.toLowerCase() === analysis.expert?.toLowerCase()
          );
          
          if (matchedExpert) {
            // Это эксперт из списка - используем его value
            finalExpert = matchedExpert.value;
            console.log('🔍 AnalysisResult - Найден эксперт в списке:', {
              original: analysis.expert,
              matchedValue: matchedExpert.value,
              matchedLabel: matchedExpert.label
            });
          } else {
            // Не найден в списке - это custom эксперт
            finalExpert = 'custom';
            finalCustomExpert = analysis.expert;
            console.log('🔍 AnalysisResult - Эксперт не найден в списке, считаем custom:', analysis.expert);
          }
        }
        
        // Обрабатываем person или expert как личность, если promptPackage = 'person'
        if (isPersonPackage) {
          // Если указан analysis.person, используем его
          const personValue = analysis.person || analysis.expert;
          
          if (personValue) {
            // Проверяем, является ли это личностью из списка (по value или label)
            const matchedPerson = persons.find(p => 
              p.value === personValue || 
              p.label.toLowerCase() === personValue.toLowerCase()
            );
            
            if (matchedPerson) {
              // Это личность из списка - используем ее value
              finalPerson = matchedPerson.value;
              console.log('🔍 AnalysisResult - Найдена личность в списке:', {
                original: personValue,
                matchedValue: matchedPerson.value,
                matchedLabel: matchedPerson.label
              });
            } else {
              // Не найдена в списке - это custom личность
              finalPerson = 'custom';
              finalCustomPerson = personValue;
              console.log('🔍 AnalysisResult - Личность не найдена в списке, считаем custom:', personValue);
            }
          }
        }
        
        // Если promptPackage не указан или 'default', используем старую логику для обратной совместимости
        if (!isExpertPackage && !isPersonPackage) {
          // Обрабатываем expert только если он указан
          if (analysis.expert) {
            // Проверяем, является ли это экспертом из списка (по value или label)
            const matchedExpert = experts.find(e => 
              e.value === analysis.expert || 
              e.label.toLowerCase() === analysis.expert?.toLowerCase()
            );
            
            if (matchedExpert) {
              // Это эксперт из списка - используем его value
              finalExpert = matchedExpert.value;
              console.log('🔍 AnalysisResult - Найден эксперт в списке (fallback):', {
                original: analysis.expert,
                matchedValue: matchedExpert.value,
                matchedLabel: matchedExpert.label
              });
            } else {
              // Проверяем, является ли это личностью
              const matchedPerson = persons.find(p => 
                p.value === analysis.expert || 
                p.label.toLowerCase() === analysis.expert?.toLowerCase()
              );
              
              if (matchedPerson) {
                // Это личность, а не эксперт - не устанавливаем expert
                finalExpert = undefined;
                if (!finalPerson) {
                  finalPerson = matchedPerson.value;
                }
                console.log('🔍 AnalysisResult - В expert передана личность, исправляем (fallback):', {
                  original: analysis.expert,
                  matchedValue: matchedPerson.value,
                  matchedLabel: matchedPerson.label
                });
              } else {
                // Не найден ни в списке экспертов, ни в списке личностей
                // Если person не указан, то это скорее всего произвольная личность (не эксперт)
                if (!analysis.person) {
                  // Это произвольная личность, а не эксперт
                  finalExpert = undefined;
                  finalPerson = 'custom';
                  finalCustomPerson = analysis.expert;
                  console.log('🔍 AnalysisResult - Не найден в списках, но person не указан - считаем произвольной личностью (fallback):', analysis.expert);
                } else {
                  // Если person указан, то это custom эксперт
                  finalExpert = 'custom';
                  finalCustomExpert = analysis.expert;
                  console.log('🔍 AnalysisResult - Эксперт не найден в списках, считаем custom (fallback):', analysis.expert);
                }
              }
            }
          }
          
          // Обрабатываем person только если он указан (и не был уже установлен из expert)
          if (analysis.person && !finalPerson) {
            const matchedPerson = persons.find(p => 
              p.value === analysis.person || 
              p.label.toLowerCase() === analysis.person?.toLowerCase()
            );
            
            if (matchedPerson) {
              // Это личность из списка - используем ее value
              finalPerson = matchedPerson.value;
              console.log('🔍 AnalysisResult - Найдена личность в списке (fallback):', {
                original: analysis.person,
                matchedValue: matchedPerson.value,
                matchedLabel: matchedPerson.label
              });
            } else {
              // Не найдена в списке - это custom личность
              finalPerson = 'custom';
              finalCustomPerson = analysis.person;
              console.log('🔍 AnalysisResult - Личность не найдена в списке, считаем custom (fallback):', analysis.person);
            }
          }
        }
        
        console.log('🔍 AnalysisResult - Финальные значения для шаблона:', {
          finalExpert,
          finalPerson,
          finalCustomExpert,
          finalCustomPerson
        });
        
        onUseImprovedTemplate(
          analysis.improvedRequest,
          finalExpert,
          finalPerson,
          finalCustomExpert,
          finalCustomPerson,
          analysis.promptPackage
        );
      } catch (error) {
        console.error('❌ Ошибка при обработке шаблона:', error);
        // В случае ошибки передаем значения как есть
        onUseImprovedTemplate(
          analysis.improvedRequest,
          analysis.expert,
          analysis.person
        );
      }
    }
  };
  

  // Всегда используем новый дизайн
  return (
      <div className="w-full">
        <div className="container mx-auto max-w-[1600px] px-2 sm:px-4 md:px-6 lg:px-12 xl:px-16 py-6 sm:py-8">
          <div className="max-w-[1400px] mx-auto w-full">
            <div className="min-h-screen py-12 space-y-16 pb-24 lg:pb-12">
          <AnalysisHeaderV3
            title={analysis.description}
            result={getAnalysisResult()}
            positiveCount={totalPlusScore}
            negativeCount={totalMinusScore}
            positiveArgumentsCount={plusItems.length}
            negativeArgumentsCount={minusItems.length}
            confidence={calculateConfidence(totalPlusScore, totalMinusScore, plusItems.length + minusItems.length)}
            theme={theme}
            hasCustomArguments={
              analysis.isModified === true || 
              analysis.items.some(item => item.isModified === true)
            }
            customArgumentsCount={analysis.items.filter(item => {
              const id = item.id && typeof item.id === 'string' ? item.id : String(item.id);
              return id.startsWith('custom_') || id.includes('expand-') || item.isExpanded === true;
            }).length}
            language={language}
            expert={getExpert()}
            llmModel={getLLMModel()}
            question={analysis.question}
            date={analysis.shareCreatedAt || analysis.createdAt}
            isOriginalModified={isOriginalModified}
            showOriginal={showOriginal}
            onToggleOriginal={onToggleOriginal}
            isTransitioning={isTransitioning}
            isLoadingOriginal={isLoadingOriginal}
            transitionDelay={0}
            plusListTitle={analysis.plusListTitle || null}
            minusListTitle={analysis.minusListTitle || null}
          />

          <ProsConsV3
            pros={plusItems}
            cons={minusItems}
            layoutMode={layoutMode}
            onLayoutChange={setLayoutMode}
            theme={theme}
            language={language}
            onAddCustomArgument={handleAddCustomArgument}
            onMoveArgument={handleMoveArgument}
            onUpdateArgumentRating={handleUpdateArgumentRating}
            onDeleteArgument={onItemDelete}
            onOpenAIPrompt={handleOpenAIPrompt}
            isReadOnly={isReadOnly}
            analysisId={analysis.external_id || analysis.id}
            analysisQuestion={analysis.question || analysis.title}
            analysisDescription={analysis.description}
            existingItems={analysis.items}
            shareId={shareId}
            isTransitioning={isTransitioning}
            transitionDelay={0.1}
            argumentSelectionLogic={analysis.argumentSelectionLogic || null}
            plusListTitle={analysis.plusListTitle || null}
            minusListTitle={analysis.minusListTitle || null}
          />

          <RecommendationV3
            mainRecommendation={analysis.recommendation}
            shortRecommendation={analysis.summary}
            keyFactors={analysis.improvementSuggestions ? [analysis.improvementSuggestions] : []}
            actionSteps={Array.isArray(analysis.actionPlan) ? analysis.actionPlan : []}
            finalDecision={getAnalysisResult()}
            originalDecision={getOriginalAnalysisResult()}
            confidence={calculateConfidence(totalPlusScore, totalMinusScore, plusItems.length + minusItems.length)}
            theme={theme}
            language={language}
            // Пропсы для шеринга
            analysis={analysis}
            onShare={onShare}
            onExportPDF={onExportPDF}
            isSharing={isSharing}
            isExportingPDF={isExportingPDF}
            shareUrl={shareUrl}
            hasBeenShared={hasBeenShared}
            shareError={shareError}
            // Пропс для нового вопроса
            onNewQuestion={onNewQuestion}
            // Пропс для shareId
            shareId={shareId}
            isTransitioning={isTransitioning}
            transitionDelay={0.2}
          />

          <ImprovementSuggestionsV3
            improvementSuggestions={analysis.improvementSuggestions}
            improvedRequest={analysis.improvedRequest}
            onUseImprovedTemplate={onUseImprovedTemplate ? handleUseImprovedTemplate : undefined}
            theme={theme}
            language={language}
            isTransitioning={isTransitioning}
            transitionDelay={0.3}
          />

          {analysis.external_id && (
            <AnalysisRating
              externalId={analysis.external_id}
              initialUserRating={analysis.userRating || null}
              theme={theme}
              language={language}
              isTransitioning={isTransitioning}
              transitionDelay={0.4}
            />
          )}

          {(showSuggestedQuestions && suggestedQuestions) && (
            <SuggestedQuestionsV3
              language={language}
              theme={theme}
              onQuestionSelect={handleSelectQuestion}
              suggestedQuestions={suggestedQuestions}
              isTransitioning={isTransitioning}
              transitionDelay={0.5}
            />
          )}

          {/* Статистика LLM */}
          {analysisTaskIds.length > 0 && (
            <LLMStatsDisplay 
              taskIds={analysisTaskIds} 
              className="mx-3 sm:mx-6"
              theme={theme}
            />
          )}

          {/* Модальные окна */}
          {aiPromptModal.isOpen && aiPromptModal.item && (
            <AIPromptModal
              isOpen={aiPromptModal.isOpen}
              onClose={handleCloseAIPrompt}
              item={aiPromptModal.item}
              analysisTitle={analysis.title}
              analysisDescription={analysis.description}
              plusListTitle={analysis.plusListTitle || null}
              minusListTitle={analysis.minusListTitle || null}
            />
          )}

          <ExpandAnalysisModal
            isOpen={expandModal}
            onClose={() => setExpandModal(false)}
            onExpand={handleExpandAnalysis}
            isLoading={isExpanding}
            currentAnalysis={analysis}
          />
            </div>
          </div>
        </div>
      </div>
    );
} 